// client/src/modules/billing/routes.tsx
import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { guardPage } from '../pageGuard';
import { BILLING_PAGES, FINANCE_PAGE } from '../pageAccess';
import { lazyWithRetry } from '../../lib/lazyWithRetry';

// Lazy load all Finance pages for code splitting
const CollectionsDashboard = lazyWithRetry(() => import('../../components/Collections/Dashboard/CollectionsDashboardV2'));
const CustomersList = lazyWithRetry(() => import('../../pages/Collections/CustomersList'));
const CollectionsWorkQueue = lazyWithRetry(() => import('../../pages/Collections/CollectionsWorkQueue'));
const CollectionsKanban = lazyWithRetry(() => import('../../pages/Collections/CollectionsKanban'));
const CustomerDetail = lazyWithRetry(() => import('../../pages/Collections/CustomerDetail'));
const CollectionsSettings = lazyWithRetry(() => import('../../pages/Collections/CollectionsSettings'));

// Invoicing
const AllInvoices = lazyWithRetry(() => import('../../pages/Collections/AllInvoices'));
const InvoiceDetail = lazyWithRetry(() => import('../../pages/Collections/InvoiceDetail'));
const ServiceInvoicing = lazyWithRetry(() => import('../../pages/Collections/ServiceInvoicing'));
const LinkReviewQueue = lazyWithRetry(() => import('../../pages/Collections/LinkReviewQueue'));
const NewInvoice = lazyWithRetry(() => import('../../pages/Billing/NewInvoice'));
const EditInvoice = lazyWithRetry(() => import('../../pages/Billing/EditInvoice'));
const InvoiceMatching = lazyWithRetry(() => import('../../pages/Projects/InvoiceMatching'));

// Invoice Drafts
const InvoiceDraftList = lazyWithRetry(() => import('../../pages/Collections/InvoiceDraftList'));
const InvoiceDraftDetail = lazyWithRetry(() => import('../../pages/Collections/InvoiceDraftDetail'));
const InvoiceDraftQueue = lazyWithRetry(() => import('../../pages/Collections/InvoiceDraftQueue'));

// Tools
const PartsRepricing = lazyWithRetry(() => import('../../pages/Billing/PartsRepricing'));
const InventoryReconciler = lazyWithRetry(() => import('../../pages/Billing/InventoryReconciler'));
const ModuleAgentFeed = lazyWithRetry(() =>
  import('../../components/shared/ModuleAgentFeed').then(m => ({ default: m.ModuleAgentFeed }))
);

/** FIN-D6: the old Ready to Bill and FSR queue addresses open Service Invoicing and keep a saved link's filters (?branch=...). */
function ServiceInvoicingRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/billing/invoicing/service${search}`} replace />;
}

/** Redirect legacy /billing/collections/customer/:id → /billing/customers/:id */
function CollectionsCustomerRedirect() {
  const { customerId } = useParams<{ customerId: string }>();
  return <Navigate to={`/billing/customers/${customerId}`} replace />;
}

/**
 * Finance module routes - relative to /billing prefix. Menu names (nav.ts) in brackets.
 *
 * Route structure:
 *   /billing/dashboard               — [Dashboard]
 *   /billing/invoicing/service       — [Invoice > Service Invoicing] (tickets grouped by the customer who pays; FIN-D6)
 *   /billing/invoicing/projects      — [Invoice > Project Invoicing] (invoice matching)
 *   /billing/customers               — [Invoice > Customers]
 *   /billing/customers/:customerId   — Customer detail
 *   /billing/invoicing               — [Invoice > Invoices] (all invoices)
 *   /billing/invoicing/new           — New invoice (invoice editor; by address only, behind the server switch)
 *   /billing/invoicing/:compositeId/edit — Edit invoice (invoice editor; by address only, behind the server switch)
 *   /billing/invoicing/:compositeId  — Invoice detail
 *   /billing/collections             — [Collect]
 *   /billing/collections/kanban      — Collect board view
 *   /billing/invoicing/link-review   — [Tools > Link Review] held invoice-to-ticket links (#3464)
 *   /billing/tools/repricing         — [Tools > Parts Repricing]
 *   /billing/tools/inventory-reconciler — [Tools > Inventory Reconciler]
 *   /billing/agent-activity          — [Tools > Agent Activity]
 *   /billing/settings                — [Settings]
 *   /billing/invoice-drafts[/queue|/:draftId] — invoice drafts (not in the menu)
 *
 * Old addresses (redirect or same screen): /invoices, /invoices/:compositeId, /fsr-queue, /invoicing/ready-to-bill,
 * /collections/settings, /collections/customer/:customerId. routes.resolve.test.tsx opens every
 * address above and every menu item.
 */
export const routes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="dashboard" replace />,
  },

  // ── AR ──────────────────────────────────────────────────────────────────
  {
    path: 'dashboard',
    ...guardPage(FINANCE_PAGE, <CollectionsDashboard />),
  },
  {
    path: 'customers',
    ...guardPage(FINANCE_PAGE, <CustomersList />),
  },
  {
    path: 'customers/:customerId',
    ...guardPage(FINANCE_PAGE, <CustomerDetail />),
  },

  // Invoicing — landing + sub-workflows
  {
    path: 'invoicing',
    ...guardPage(FINANCE_PAGE, <AllInvoices />),
  },
  {
    path: 'invoicing/service',
    ...guardPage(BILLING_PAGES.serviceInvoicing, <ServiceInvoicing />),
  },
  {
    path: 'invoicing/ready-to-bill',
    element: <ServiceInvoicingRedirect />,
  },
  {
    path: 'invoicing/projects',
    ...guardPage(BILLING_PAGES.projectInvoicing, <InvoiceMatching />),
  },
  {
    // Kept above ':compositeId' for readability. The router ranks a fixed word above an id, so order alone
    // does not decide it (measured 2026-10-15); routes.resolve.test.tsx fails if "link-review" opens an invoice.
    path: 'invoicing/link-review',
    ...guardPage(FINANCE_PAGE, <LinkReviewQueue />),
  },
  {
    // New invoice (the invoice editor). Reached by address only; not in the menu until the finance-nav design (FIN-D10) places it.
    // The server switch appConfig/invoiceEditor decides whether it works: while OFF the page says so and offers nothing.
    path: 'invoicing/new',
    ...guardPage(FINANCE_PAGE, <NewInvoice />),
  },
  {
    // Edit an existing invoice (the invoice editor). Reached by address only until the finance-nav design places a button.
    // The server switch appConfig/invoiceEditor (editEnabled) decides whether it works: while OFF the page says so and offers nothing.
    path: 'invoicing/:compositeId/edit',
    ...guardPage(FINANCE_PAGE, <EditInvoice />),
  },
  {
    path: 'invoicing/:compositeId',
    ...guardPage(FINANCE_PAGE, <InvoiceDetail />),
  },

  // Invoice Drafts
  {
    path: 'invoice-drafts',
    ...guardPage(FINANCE_PAGE, <InvoiceDraftList />),
  },
  {
    // Kept above ':draftId' for readability; the router ranks "queue" above an id regardless.
    // routes.resolve.test.tsx fails if "queue" opens a draft called "queue".
    path: 'invoice-drafts/queue',
    ...guardPage(FINANCE_PAGE, <InvoiceDraftQueue />),
  },
  {
    path: 'invoice-drafts/:draftId',
    ...guardPage(FINANCE_PAGE, <InvoiceDraftDetail />),
  },

  // Collections
  {
    path: 'collections',
    ...guardPage(FINANCE_PAGE, <CollectionsWorkQueue />),
  },
  {
    path: 'collections/kanban',
    ...guardPage(FINANCE_PAGE, <CollectionsKanban />),
  },
  {
    path: 'collections/customer/:customerId',
    element: <CollectionsCustomerRedirect />,
  },

  // ── Tools ───────────────────────────────────────────────────────────────
  {
    path: 'tools/repricing',
    ...guardPage(FINANCE_PAGE, <PartsRepricing />),
  },
  {
    path: 'tools/inventory-reconciler',
    ...guardPage(FINANCE_PAGE, <InventoryReconciler />),
  },

  // ── Settings ────────────────────────────────────────────────────────────
  {
    path: 'settings',
    ...guardPage(FINANCE_PAGE, <CollectionsSettings />),
  },

  // ── Legacy routes (preserve existing links across the codebase) ─────────
  // These keep old URLs working until all references are migrated
  {
    path: 'invoices',
    element: <Navigate to="/billing/invoicing" replace />,
  },
  {
    path: 'invoices/:compositeId',
    ...guardPage(FINANCE_PAGE, <InvoiceDetail />),
  },
  {
    path: 'fsr-queue',
    element: <ServiceInvoicingRedirect />,
  },
  {
    path: 'collections/settings',
    element: <Navigate to="/billing/settings" replace />,
  },

  // ── Agent Activity ─────────────────────────────────────────────────────
  {
    path: 'agent-activity',
    ...guardPage(FINANCE_PAGE, <ModuleAgentFeed moduleId="P4" />),
  },

  // ── Fallback ────────────────────────────────────────────────────────────
  {
    path: '*',
    element: <Navigate to="/billing/dashboard" replace />,
  },
];
